<template>
  <tr v-if="shouldShowRow" :class="setRowClass" @click.stop="$emit('viewContent', contents)" style="cursor:pointer;">
    <td class="py-3 align-middle text-left" v-if="isAdmin" style="padding-left: 31px;">
      <input type="checkbox" class="claims-checkbox" :checked="isSelected" @click.stop
        @change="$emit('toggleSelection', contents.id, $event.target.checked)" />
    </td>

    <td class="text-nowrap">
      <div class="d-lg-flex d-block gap-2 align-items-center justify-content-start">
        <MDBBtn class="fw-bold mb-lg-0 mb-2 bg-none shadow-0 fw-bold px-0" style="color: var(--primary);" size="sm"
          @click.stop="$emit('viewContent', contents)">
          <MDBIcon iconStyle="fas" icon="eye" style="margin-right: 3px;" />
          View Details
        </MDBBtn>
      </div>
    </td>

    <td class="text-nowrap" v-if="isAdmin">
      <div class="d-flex align-items-center gap-2">
        <span class="text-wrap: nowrap;">
          {{ contents.userId ? contents.userFirstName + ' ' + contents.userLastName : '—' }}
        </span>
        <span v-if="contents.assetLabel" @click.stop="copy(contents.assetLabel)"
          class="px-3 d-flex align-items-center rounded-4 py-1"
          style="background: var(--primary); color: white; font-size:12px; border-radius: 10px;cursor: pointer;"
          title="Click to Copy Asset Label">
          {{ contents.assetLabel }}
          <vue-feather type="copy" size="12" :stroke-width="2.5" class="ms-2" />
        </span>
      </div>
    </td>

    <td class="text-nowrap">
      <div class="d-flex flex-column justify-content-between">
        <div>
          <MDBBadge v-if="contents.claimStatusName" :color="setBadgeColor" class="rounded-circle me-2" dot
            style="position: relative;margin-left:0;"></MDBBadge>
          <span class="fw-bold">
            {{ contents.claimStatusName ? contents.claimStatusName : '—' }}
          </span>
        </div>
        <span v-if="contents.claimStatus === '1' && contents.issuanceTime != 0" style="color: black;font-size: 12px;"
          class="ms-3">
          {{ `${convertToReadableTime(contents.issuanceTime)} claim time` }}
        </span>
        <span v-if="contents.claimStatus === '5'" style="color: black;font-size: 12px;" class="ms-3">
          {{ timeAgo(contents.dateAdded) }}
        </span>
        <span v-if="contents.claimStatus === '4'" style="color: black;font-size: 12px;" class="ms-3">
          {{ timeAgo(contents.issueFixDate) }}
        </span>
      </div>
    </td>

    <td class="text-nowrap">{{ contents.claimStatusName ? contents.claimStatusName : '—' }}</td>
    <td class="equal-width">
      <img v-if="contents.thumbnail" :src="contents.thumbnail" alt="thumbnail" style="width: 30px; height: 30px"
        class="rounded-circle me-2" />
      <img v-else src="@/assets/user-default.png" alt="thumbnail" style="width: 30px; height: 30px"
        class="rounded-circle me-2" />
      <span>{{ contents.youtubeChannel ? contents.youtubeChannel : '—' }}</span>
    </td>

    <td class="equal-width" :title="contents.youtubeUrl">
      <a :href="contents.youtubeUrl" target="_blank" :title="contents.youtubeUrl">
        {{ contents.youtubeUrl ? contents.youtubeUrl : '—' }}
      </a>
    </td>

    <td class="text-nowrap">{{ contents.timeStamp ? contents.timeStamp : '—' }}</td>

    <td class="equal-width" :title="contents.dropboxLink">
      <a :href="contents.dropboxLink" target="_blank" :title="contents.dropboxLink">
        {{ contents.dropboxLink ? contents.dropboxLink : '—' }}
      </a>
    </td>

    <template v-if="isAdmin">
      <td class="equal-width" :title="contents.internalAssetDescription">
        {{ contents.internalAssetDescription ? contents.internalAssetDescription : '—' }}
      </td>
      <td class="equal-width">{{ contents.clipTitle ? contents.clipTitle : '—' }}</td>
      <td class="text-nowrap">{{ contents.assetId ? contents.assetId : '—' }}</td>
      <td class="text-nowrap">{{ contents.dateClaimed ? parseDateLonger(contents.dateClaimed) : '—' }}</td>
      <td class="text-nowrap">{{ contents.claimantFirstName ? contents.claimantFirstName + ' ' +
        contents.claimantLastName : '—' }}</td>
      <td class="equal-width">{{ contents.remarks ? contents.remarks : '—' }}</td>
      <td class="equal-width">{{ contents.notes ? contents.notes : '—' }}</td>
    </template>

    <td class="text-nowrap">{{ contents.dateAdded ? parseDateLonger(contents.dateAdded) : '—' }}</td>
    <td v-if="isAdmin" class="text-nowrap">{{ contents.dateUpdated ? parseDateLonger(contents.dateUpdated) : '—' }}</td>
  </tr>
</template>


<script setup>
import "./claims-checkbox.css";
import { convertToReadableTime, parseDateLonger, timeAgo } from "@/helpers/parseDate";
import { useUserDetailStore } from "@/store/userDetailStore";
import { useClipboard } from "@vueuse/core";
import {
  MDBBadge,
  MDBBtn,
  MDBIcon
} from "mdb-vue-ui-kit";
import { computed, defineEmits, defineProps, ref, watch } from "vue";
import VueFeather from 'vue-feather';

const { copy, copied } = useClipboard({ legacy: true })

watch(copied, (value) => {
  if (value) {
    alert("Copied");
  }
});
const filter = ref('all');

const props = defineProps({
  isAdmin: {
    type: Boolean,
    required: true,
  },
  isSelected: {
    type: Boolean,
    required: false,
  },
  contents: {
    type: Object,
    required: true,
    default: () => ({
      id: 0,
      userFirstName: '',
      userId: 0,
      userLastName: '',
      userAdded: 0,
      youtubeChannel: '',
      youtubeUrl: '',
      timeStamp: '',
      assetLabel: '',
      dropboxLink: '',
      internalAssetDescription: '',
      clipTitle: '',
      claimStatus: '',
      claimStatusName: '',
      assetId: '',
      claimant: '',
      claimantFirstName: '',
      claimantLastName: '',
      dateClaimed: null,
      remarks: '',
      notes: '',
      dateAdded: '',
      dateUpdated: '',
    }),
  }
});


const shouldShowRow = computed(() => {
  const claimant = props.contents.claimant?.toString();
  const currentUserId = userDetailStore.userDetail.id.toString();

  if (filter.value === 'all') return true;

  if (filter.value === 'unclaimed') {
    return !claimant;
  } else if (filter.value === 'mine') {
    return claimant === currentUserId;
  } else if (filter.value === 'others') {
    return claimant && claimant !== currentUserId;
  }
  return true;
});
defineEmits(["viewContent", "toggleSelection"]);

const userDetailStore = useUserDetailStore();
const setRowClass = ref();
watch(userDetailStore.userDetail, (newDetail) => {
  if (props.isAdmin) {
    if (props.contents.claimant && props.contents.claimant !== newDetail.id.toString()) {
      setRowClass.value = 'claimedByOthers';
    } else if (props.contents.claimant && props.contents.claimant === newDetail.id.toString()) {
      setRowClass.value = 'claimedByYou';
    }
  } else {
    if (props.contents.claimant) {
      setRowClass.value = 'claimed';
    }
  }
}, { immediate: true });
const setBadgeColor = computed(() => {
  if (props.contents.claimStatus === "1") {
    return 'success';
  } else if (props.contents.claimStatus === "2") {
    return 'danger';
  } else {
    return 'warning';
  }
})

</script>

<style scoped>
td {
  white-space: normal;
  word-break: break-word;
  font-size: 13px;
}

tr {
  background: white;
}

.claimedByOthers {
  background-color: #FFF4D7;
}

.claimedByYou,
.claimed {
  background-color: #D7DEFF;
  color: var(--primary);
}

.btn-primary {
  background-color: var(--primary);
  display: flex;
  align-items: center;
  gap: 5px;
}

.action {
  width: 200px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 5px;
}

.no-wrap-td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.equal-width {
  width: 270px;
  min-width: 270px;
  max-width: 270px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
